import React, { useState, useEffect, useRef } from 'react';
import { useHitTracking } from '../../context/hit-tracking-context';
import { Card, Button, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquareCheck, faGripDotsVertical, faTrash, faPlus, faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { faCheck, faEllipsisVertical, faHexagonCheck, faPen, faPencil } from '@fortawesome/pro-regular-svg-icons';
import AddTransactionModal from './components/AddTransactionModal';
import { PortfolioServiceApi, Configuration } from "../../services/portfolio";
import { APIControl } from '../../helpers/APIControl';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { removePortfolio, renamePortfolio, setSelectedPortfolio } from '../../stores/portfolioSlice';
import CreatePortfolioModal from './components/CreatePortfolioModal';
import dayjs from 'dayjs';
import NumberFormatter from '../../formatters/NumberFormatter';
import RemovePopupModal from '../../components/alert/RemovePopupModal';
import Spinner from '../../components/spinner';
import { Toast } from 'primereact/toast';
import { useTranslation } from 'react-i18next';
import { get } from 'lodash';


const Portfolios = () => {
    const { t } = useTranslation();
    const { trackInteraction } = useHitTracking();
    const portfolioServiceApi = new PortfolioServiceApi(new Configuration({ basePath: process.env.REACT_APP_PORTFOLIO_API_BASE_PATH }), undefined, APIControl(process.env.REACT_APP_PORTFOLIO_API_BASE_PATH));
    const token = useSelector((state) => state.auth?.token);

    const [isEditing, setIsEditing] = useState(false);
    const [menuVisible, setMenuVisible] = useState(null);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const [showAddTransactionModal, setShowAddTransactionModal] = useState(false);
    const [showCreatePortfolioModal, setShowCreatePortfolioModal] = useState(false);

    const [portfolioData, setPortfolioData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [transactionData, setTransactionData] = useState([]);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [hasTransaction, setHasTransaction] = useState(false);
    const dispatch = useDispatch();
    const selectedPortfolioId = useSelector((state) => state.portfolio.selectedPortfolioId);
    const [transactionItemMenuVisible, setTransactionItemMenuVisible] = useState(null);
    const [transactionItemMenuPosition, setTransactionItemMenuPosition] = useState({ top: 0, left: 0 });
    const numberFormatter = new NumberFormatter();
    const [isPortfolioLoading, setIsPortfolioLoading] = useState(true);
    const [isTransactionLoading, setIsTransactionLoading] = useState(true);
    const [selectedPortfolioToDelete, setSelectedPortfolioToDelete] = useState(null);
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [selectedTransactionToDelete, setSelectedTransactionToDelete] = useState(null); // Yeni state
    const toast = useRef(null);
    const menuRef = useRef(null);
    const [openUpward, setOpenUpward] = useState(false);
    const transactionMenuRef = useRef(null);
    const [valuation, setValuation] = useState({
        latest: 0,        
        dailyValue: 0,     
        dailyPct: 0,       
        totalValue: 0,    
        totalPct: 0,        
    });
    const dummyData = [
        {
            id: 1,
            description: portfolioData.find(p => p.id === selectedPortfolioId)?.name || '',
            price: numberFormatter.Double(
                portfolioData.find(p => p.id === selectedPortfolioId)?.netTransactionValue || 0,
                2
            ),
            nominal: 0.00,
            commissionRate: '000.00',
            dailyAmount: -0.00,
            totalAmount: '000.00',
            dailyPositive: false,
            totalPositive: true,
        },
    ];

    const getInstrumentType = (tx) => {
      if (tx.entityId === 'cur.1')                    return 'cur.1';      // nakit
      if (tx.entityUniqueCode?.includes(':FX') || tx.entityUniqueCode?.includes(':X5') )      return 'fx';        // döviz
      if (tx.entityUniqueCode?.includes(':TMF'))     return 'fund';      // fon
      return 'equity';                                                   // hisse
    };
    
    const decimalsFor = (tx) => {
      switch (getInstrumentType(tx)) {
        case 'fx':   return 4;   // döviz → 4 hane
        case 'fund': return 6;   // fon   → 6 hane
        default:     return 2;   // diğer → 2 hane
      }
    };


    useEffect(() => {
        fetchPortfoliosData();
    }, []);

    useEffect(() => {
        if (portfolioData.length > 0 && !selectedPortfolioId) {
            dispatch(setSelectedPortfolio(portfolioData[0].id));
        }
    }, [portfolioData, selectedPortfolioId, dispatch]);

    useEffect(() => {
        if (selectedPortfolioId) {
            fetchPortfoliosTransactionsData(selectedPortfolioId);
        }
    }, [selectedPortfolioId]);

    useEffect(()=>{
        if(selectedPortfolioId) fetchSimulation(selectedPortfolioId);
    }, [selectedPortfolioId, transactionData]);   // işlem eklenince de güncellensin

    const handleTransactionAdd = (selectedSecurity) => {
        setSelectedTransaction(selectedSecurity);
        setHasTransaction(true);
        fetchPortfoliosData(); // Refresh portfolios to update netTransactionValue
    };


    //#region Left Card Functions
    const fetchPortfoliosData = async () => {
        const valuationDate = dayjs().format('YYYYMMDD');
        setIsPortfolioLoading(true);
        try {
        const response = await portfolioServiceApi.v1PortfoliosGet(token,valuationDate, 3);
        const portfolios = response.data.portfolios.map((p) => {
            let simVal = null;
            if (p.simulation?.dailyValuations?.length) {
                const latest = [...p.simulation.dailyValuations].sort((a, b) => b.date - a.date)[0];
                simVal = latest?.value ?? null;
            }
            return {
                id:               p.id,
                name:             p.name,
                transactionCount: p.transactionCount,
                readOnly:         p.readOnly,
                netTransactionValue: p.netTransactionValue,
                displayValue:     simVal ?? p.netTransactionValue,
            };
            
        });
        setPortfolioData(portfolios);
        if (!selectedPortfolioId && portfolios.length) {
            dispatch(setSelectedPortfolio(portfolios[0].id));
        }
        setHasTransaction(portfolios.length > 0);
        } catch (error) {
            const errorMessage = error.response?.data?.message || "Portföy verileri alınırken bir hata oluştu.";
            toast.current.show({ severity: 'error', summary: 'Hata', detail: errorMessage, life: 3000 });
            console.error(error);
        } finally {
            setIsPortfolioLoading(false);
        }
    };

    const handleRename = (id) => {
        trackInteraction('menu/renamePortfolio');
        setPortfolioData(
            portfolioData.map((portfolio) =>
                portfolio.id === id ? { ...portfolio, isRenaming: true } : { ...portfolio, isRenaming: false }
            )
        );
        setMenuVisible(null);
    };


    const handleDelete = (id) => {
        trackInteraction('menu/deletePortfolio');
        setSelectedPortfolioToDelete(id); // Silinecek portföyü seç
        setShowRemoveModal(true); // Onay modunu aç
        setMenuVisible(null);
    };

    const confirmDelete = async () => {
        if (selectedPortfolioToDelete) {
            await removePortfolioList(selectedPortfolioToDelete);
            setShowRemoveModal(false);
        }
    };


    const handleNameChange = (id, newName) => {
        setPortfolioData(
            portfolioData.map((portfolio) =>
                portfolio.id === id ? { ...portfolio, name: newName } : portfolio
            )
        );
    };

    const handleRenameSubmit = (id) => {
        const portfolio = portfolioData.find((p) => p.id === id);
        if (portfolio) {
            handleRenamePortfolio(id, portfolio.name);
        }

        setPortfolioData(
            portfolioData.map((portfolio) =>
                portfolio.id === id ? { ...portfolio, isRenaming: false } : portfolio
            )
        );
    };

    const handleAddTransactionClick = () => {
        trackInteraction('button/addTransaction');
        setShowAddTransactionModal(true);
        setSelectedTransaction(null);
    };



    const toggleEditMode = () => {
        trackInteraction(`button/${isEditing ? 'exitEditMode' : 'editMode'}`);
        setIsEditing(!isEditing);
        setMenuVisible(null);
    };

    const handleMenuToggle = (id, event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        setMenuPosition({
            top: rect.bottom + window.scrollY + 14,
            left: rect.right + window.scrollX - 10,
        });
        setMenuVisible(menuVisible === id ? null : id);
    };


    const handlePortfolioCreated = (success) => {
        setShowCreatePortfolioModal(false);
        if (success) {
            fetchPortfoliosData(); // Yeni bir portföy eklendiğinde listeyi güncelle
        }
    };

    const handleAddPortfolioClick = () => {
        trackInteraction('button/createPortfolio');
        setShowCreatePortfolioModal(true);
    };

    const removePortfolioList = async (id) => {
        try {
            const requestBody = { portfolioId: id };
            const response = await portfolioServiceApi.v1PortfolioDeletePost(token, requestBody);

            if (response.data) {
                const updatedPortfolios = portfolioData.filter((portfolio) => portfolio.id !== id);
                setPortfolioData(updatedPortfolios);

                if (updatedPortfolios.length === 0) {
                    dispatch(removePortfolio());
                } else {
                    dispatch(setSelectedPortfolio(updatedPortfolios[0].id));
                }
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || "Portföy silinirken bir hata oluştu.";
            toast.current.show({ severity: 'error', summary: 'Hata', detail: errorMessage, life: 3000 });
            console.error(error);
        }

    };

    const handleRenamePortfolio = async (id, newName) => {
        try {
            const requestBody = { portfolioId: id, name: newName };
            const response = await portfolioServiceApi.v1PortfolioRenamePost(token, requestBody);

            if (response.data) {
                dispatch(renamePortfolio({ id, name: newName })); // Redux durumunu güncelle
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || "Portföy ismi güncellenirken bir hata oluştu.";
            toast.current.show({ severity: 'error', summary: 'Hata', detail: errorMessage, life: 3000 });
            console.error('handleRenamePortfolio error:', error);
        }
    };

    //#endregion Left Card Functions

    //#region Right Card Functions

    const fetchSimulation = async (pid) => {
      try {
        const today = parseInt(dayjs().format('YYYYMMDD'));
        const { data } = await portfolioServiceApi.v1PortfolioSimulateGet(
          token,
          pid,
          today,
          3     
        );
        const vals   = [...data.dailyValuations].sort((a,b)=>a.date-b.date);
        const lastVal  = vals.at(-1);       
        const prevVal   = vals.length > 1 ? vals.at(-2) : null;
        const thirdVal = vals.length > 1 ? vals.at(-3) : null;
        const hasData   = !!lastVal;      
        console.log('lastval: ', lastVal);
        console.log('prevval: ', prevVal);
        console.log('thirdVal: ', thirdVal);
            setValuation({
              latest:      hasData ? lastVal.value : 0,      
              dailyValue: prevVal ? prevVal.pnl : 0,
              dailyPct:   prevVal ? prevVal.pnlRate*100 : 0,
              totalValue:  data.pnl ?? 0,
              totalPct:    (data.pnlRate ?? 0) * 100,
            });

        }
       catch(err){ 
        setValuation({
          latest: 0,
          dailyValue: 0,
          dailyPct: 0,
          totalValue: 0,
          totalPct: 0,
        });
        console.error(err);
     }
    };

    const handleTransactionItemMenuToggle = (id, event) => {
      const buttonRect = event.currentTarget.getBoundingClientRect();
      const estimatedMenuHeight = 90;
        
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      const openUp = spaceBelow < estimatedMenuHeight;
        
      setOpenUpward(openUp);
        
      setTransactionItemMenuPosition({
        top: openUp
          ? buttonRect.top - estimatedMenuHeight + window.scrollY
          : buttonRect.bottom + 5 + window.scrollY,
        left: buttonRect.left - 75 + window.scrollX,
      });
  
      setTransactionItemMenuVisible(transactionItemMenuVisible === id ? null : id);
    };




    // Right Card Functions


    const fetchPortfoliosTransactionsData = async () => {
        setIsTransactionLoading(true);
        try {
            const response = await portfolioServiceApi.v1TransactionsGet(token, selectedPortfolioId);
            const transactions = response.data.transactions.map((transaction) => ({
                id: transaction.id,
                date: transaction.date,
                price: transaction.price,
                close: transaction.close,
                nominal: transaction.nominal,
                commissionRate: transaction.commissionRate,
                description: transaction.description,
                code: transaction.entityId,
                entityCode: transaction.entityCode,
                entityId: transaction.entityId,
                currencyAmount: transaction.currencyAmount,
                entityUniqueCode: transaction.entityUniqueCode
            }));
            setTransactionData(transactions);
            console.log("setTransactionData: ", transactions);
            setHasTransaction(transactions.length > 0);
            await fetchSimulation(selectedPortfolioId);
        } catch (error) {
            const errorMessage = error.response?.data?.message || "İşlem verileri alınırken bir hata oluştu.";
            toast.current.show({ severity: 'error', summary: 'Hata', detail: errorMessage, life: 3000 });
            console.error('fetchPortfoliosTransactionsData error:', error);
        } finally {
            setIsTransactionLoading(false);
        }
    };

    const handleDeleteTransaction = (transaction) => {
        console.log("handleDeleteTransaction called with transaction:", transaction);
        trackInteraction('menu/deleteTransaction');
        setSelectedTransactionToDelete(transaction); // Silinecek işlemi seçiyoruz
        setShowRemoveModal(true); // Onay modunu açıyoruz
        setTransactionItemMenuVisible(null); // Menü görünümünü kapatıyoruz
    };

    const confirmDeleteTransaction = async () => {
        console.log("confirmDeleteTransaction", selectedTransactionToDelete);
        
        if (selectedTransactionToDelete) {
            const requestBody = { transactionId: selectedTransactionToDelete.id };
            try {
                const response = await portfolioServiceApi.v1TransactionDeletePost(token, requestBody);
                if (response.data) {
                    setTransactionData(transactionData.filter((transaction) => transaction.id !== selectedTransactionToDelete.id));
                }
            } catch (error) {
                console.error("Transaction delete error:", error);
            } finally {
                setShowRemoveModal(false); // Modalı kapat
                setSelectedTransactionToDelete(null); // Seçili transaction'ı sıfırla
            }
        }
    };


    const handleRenameTransaction = (transaction) => {
        trackInteraction('menu/editTransaction');
        setSelectedTransaction(transaction);
        setShowAddTransactionModal(true);
        setTransactionItemMenuVisible(null);
    };


    const handleCloseAddTransactionModal = () => {
        setShowAddTransactionModal(false);
    };
    //#endregion Right Card Functions

    // Add a function to calculate the portfolio total including cash transactions
    const calculatePortfolioTotal = (transactions) => {
        if (!transactions || transactions.length === 0) return 0;

        return transactions.reduce((total, transaction) => {
            if (transaction.entityId === "cur.1" || transaction.entityCode === "cur.1") {
                return total + transaction.price;
            }

            // Currency işlemleri
            if (transaction.nominal === 1 && transaction.currencyAmount) {
                return total + (transaction.price * transaction.currencyAmount);
            }

            // Diğer işlemler (komisyon dahil)
            const commission = parseFloat(transaction.commissionRate || 0);
            const multiplier = 1 + (commission / 100);

            return total + (transaction.price * transaction.nominal * multiplier);
        }, 0);
    };

    // Add helper function to convert YYYYMMDD format to Date object
    const convertYYYYMMDDToDate = (yyyymmdd) => {
        if (!yyyymmdd) return new Date();

        // Extract year, month, and day from integer
        const yearStr = String(yyyymmdd);
        if (yearStr.length !== 8) return new Date(); // Invalid format

        const year = parseInt(yearStr.substring(0, 4));
        const month = parseInt(yearStr.substring(4, 6)) - 1; // JS months are 0-indexed
        const day = parseInt(yearStr.substring(6, 8));

        return new Date(year, month, day);
    };

    return (
        <div className="portfoliosContainer">
            <Toast ref={toast} />

            <Row>
                <Col md={3}>
                    <div className="portfolioHeader left d-flex justify-content-between align-items-center mb-2">
                        <label className="title">Portföyler</label>
                        {isEditing ? (
                            <FontAwesomeIcon icon={faSquareCheck} onClick={toggleEditMode} className="edit-icon " />
                        ) : (
                            <FontAwesomeIcon icon={faPen} onClick={toggleEditMode} className="edit-icon pen" />
                        )}
                    </div>


                    <Card className="portfolioCard">
                        <Card.Body className='cardBodyLeft'>
                            {isPortfolioLoading ? (
                                <div className="d-flex justify-content-center align-items-center m-t-50">
                                    <Spinner />
                                </div>
                            ) : (
                                portfolioData.map((portfolio) => (
                                    <div
                                        key={portfolio.id}
                                        className={`portfolioItem d-flex align-items-center justify-content-between ${selectedPortfolioId === portfolio.id ? 'active' : ''}`}
                                        onClick={() => { trackInteraction(`portfolio/select`); dispatch(setSelectedPortfolio(portfolio.id)); }}
                                    >
                                        {isEditing && (
                                            <FontAwesomeIcon icon={faGripDotsVertical} className="grip-icon" />
                                        )}
                                        <div className="portfolioInfo">
                                            {portfolio.isRenaming ? (
                                                <input
                                                    type="text"
                                                    className="form-control portfolioNameInput"
                                                    value={portfolio.name}
                                                    onChange={(e) => handleNameChange(portfolio.id, e.target.value)}
                                                />
                                            ) : (
                                                <Card.Title className="portfolioName">{portfolio.name}</Card.Title>
                                            )}
                                            <Card.Subtitle className="text-muted portfolioBalance">
                                                {numberFormatter.Double(portfolio.displayValue, 2)} TL
                                            </Card.Subtitle>
                                        </div>
                                        {isEditing && (
                                            <>

                                                {
                                                    portfolio.isRenaming ? (
                                                        <FontAwesomeIcon
                                                            icon={faSquareCheck}
                                                            className="check-icon-rename"
                                                            onClick={() => handleRenameSubmit(portfolio.id)}
                                                        />
                                                    ) : (
                                                        <div className='editIconContainer' onClick={(e) => handleMenuToggle(portfolio.id, e)}>
                                                            <FontAwesomeIcon
                                                                icon={faEllipsisVertical}
                                                                className={`ellipsis-icon ${menuVisible === portfolio.id ? 'active' : ''}`}

                                                            />
                                                        </div>
                                                    )
                                                }

                                            </>)}
                                    </div>
                                ))
                            )}
                            <div className="divider-line m-t-10"></div>
                            <Button variant="success" className="createPortfolioButton mt-3 custom-button" onClick={handleAddPortfolioClick}>
                                Portföy Oluştur
                            </Button>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={9} className="portfolioCardRight">
                    {!hasTransaction ? (
                        <Card className="portfolioCardEmpty">
                            <Card.Body className="portfolioContentEmpty">
                                <div className='portfolioEmptyText'>Portföyünüz halen boş, lütfen işlem ekleyin.</div>
                                <Button variant="success" className="addTransactionButton custom-button" onClick={handleAddTransactionClick}>
                                    İşlem Ekle
                                </Button>
                            </Card.Body>
                        </Card>
                    ) : (
                        <>
                            <div className="portfolioHeader">
                                <Card className="portfolioCard top">
                                  <Card.Body className="d-flex justify-content-between align-items-center">
                                    <div>
                                      <label className="title">
                                        {portfolioData.find(p=>p.id===selectedPortfolioId)?.name}
                                      </label>
                                          <div className="price">
                                            {numberFormatter.Double(valuation.latest, 2)} TL
                                          </div>

                                          {/* Yatırıma Yönlendirilen Tutar */}
                                          <div className="invested">
                                            <small className="text-muted">Yatırıma Yönlendirilen Tutar:&nbsp;</small>
                                            <small>
                                              {numberFormatter.Double(
                                                  portfolioData.find(p=>p.id===selectedPortfolioId)?.netTransactionValue ?? 0,
                                                  2)} TL
                                            </small>
                                          </div>
                                    </div>
                                        
                                   <div className="change-summary">
                                      <div className="change-item">
                                        <label className="profitLostLabel">Günlük Kar/Zarar</label>
                                        {valuation.latest != null ? (() => {
                                          const pctSign = valuation.dailyPct < 0 ? '-' : '';
                                          const valSign = valuation.dailyValue < 0 ? '-' : '';
                                          return (
                                            <div className={`badge ${valuation.dailyValue >= 0 ? 'badge-success' : 'badge-danger'}`}>
                                              {pctSign}%{numberFormatter.Double(Math.abs(valuation.dailyPct), 2)}
                                              &nbsp; / &nbsp;
                                              {valSign}{numberFormatter.Double(Math.abs(valuation.dailyValue), 2)} TL
                                            </div>
                                          );
                                        })() : '-'}
                                      </div>
                                    
                                      <div className="change-item">
                                        <label className="profitLostLabel">Toplam Kar/Zarar</label>
                                        {valuation.latest != null ? (() => {
                                          const pctSign = valuation.totalPct < 0 ? '-' : '';
                                          const valSign = valuation.totalValue < 0 ? '-' : '';
                                          return (
                                            <div className={`badge ${valuation.totalValue >= 0 ? 'badge-success' : 'badge-danger'}`}>
                                              {pctSign}%{numberFormatter.Double(Math.abs(valuation.totalPct), 2)}
                                              &nbsp; / &nbsp;
                                              {valSign}{numberFormatter.Double(Math.abs(valuation.totalValue), 2)} TL
                                            </div>
                                          );
                                        })() : '-'}
                                      </div>
                                    </div>

                                  </Card.Body>
                                </Card>
                                <Button
                                    variant="success"
                                    className="addTransactionButtonTopCard custom-button"
                                    onClick={() => {
                                        setShowAddTransactionModal(true);
                                        setSelectedTransaction(null);  // Yeni işlem için transaction boş olsun
                                    }}
                                >
                                    <FontAwesomeIcon icon={faPlus} /> İşlem Ekle
                                </Button>
                            </div>

                            {/* İşlem Listesi Kartı */}
                            <Card className="portfolioCardRightCenter">
                                <Card.Body className="portfolioContent">
                                    {isTransactionLoading ? (
                                        <div className="d-flex justify-content-center align-items-center m-t-50">
                                            <Spinner />
                                        </div>
                                    ) : (
                                        <table className="table table-borderless">
                                            <thead style={{ backgroundColor: '#f0f0f0', color: '#6c757d' }}>
                                                <tr>
                                                    <th style={{ textAlign: 'left', width: '55px' }}>{t('portfoliosTable.security')}</th>
                                                    <th style={{ textAlign: 'center' }}>{t('portfoliosTable.date')}</th>
                                                    <th style={{ textAlign: 'right' }}>{t('portfoliosTable.quantity')}</th>
                                                    <th style={{ textAlign: 'right' }}>{t('portfoliosTable.transactionPrice')}</th>
                                                    <th style={{ textAlign: 'right' }}>{t('portfoliosTable.lastPrice')}</th>
                                                    <th style={{ textAlign: 'right' }}>{t('portfoliosTable.commission')} (%)</th>
                                                    <th style={{ textAlign: 'right' }}>{t('portfoliosTable.totalAmount')} (TL)</th>
                                                    <th style={{ textAlign: 'center', width: '35px' }}>{t('portfoliosTable.actions')}</th>
                                                </tr>
                                            </thead>


                                            <tbody>
                                                  {transactionData.map((tx) => (
                                                    <tr key={tx.id}>                                                  
                                                      <td className="tableCell" style={{ textAlign: 'left', width: '55px' }}>
                                                        {tx.code === 'cur.1' ? t('portfolio.cash') : tx.entityCode}
                                                      </td>
                                                                                                
                                                      <td className="tableCell" style={{ textAlign: 'center' }}>
                                                        {dayjs(convertYYYYMMDDToDate(tx.date)).format('D.MM.YYYY')}
                                                      </td>
                                                    <td className="tableCell" style={{ textAlign: 'right' }}>
                                                        {tx.entityId === 'cur.1' 
                                                          ? `-`
                                                          : tx.currencyAmount
                                                             ? `${tx.currencyAmount < 0 ? '-' : ''}${numberFormatter.Double(Math.abs(tx.currencyAmount), 2)}`
                                                              : `${tx.nominal < 0 ? '-' : ''}${numberFormatter.Double(Math.abs(tx.nominal), 0)}`}
                                                    </td>                        
                                                      <td className="tableCell" style={{ textAlign: 'right' }}>
                                                        {tx.entityId === 'cur.1'
                                                          ? '-'
                                                          : `${numberFormatter.Double(parseFloat(Math.abs(tx.price)), decimalsFor(tx))}`}
                                                      </td>
                                                                                                

                                                     <td className="tableCell" style={{ textAlign:'right' }}>
                                                       {tx.entityId === 'cur.1'
                                                         ? '-'
                                                         : `${numberFormatter.Double(+tx.close,decimalsFor(tx))}`}
                                                     </td>
                                                        
                                                      <td className="tableCell" style={{ textAlign: 'right' }}>
                                                        {tx.entityCode === 'cur.1' || tx.currencyAmount
                                                          ? '-'
                                                          : `${numberFormatter.Double((parseFloat(tx.commissionRate * 100) || 0), 2)}`}
                                                      </td>

                                                      <td
                                                        className={`tableCell ${
                                                          (tx.entityCode === 'cur.1' || tx.currencyAmount)
                                                            ? (tx.price > 0 || (tx.currencyAmount && tx.currencyAmount > 0) ? 'text-success' : 'text-danger')
                                                            : ''
                                                        }`}
                                                        style={{ textAlign: 'right' }}
                                                      >
                                                        {tx.entityId === 'cur.1' && (
                                                          `${tx.nominal < 0 ? '-' : ''}${numberFormatter.Double(Math.abs(tx.nominal), 2)}`
                                                        )}

                                                        {tx.currencyAmount && tx.entityId !== 'cur.1' && (
                                                          `${tx.nominal > 0 ? '' : '-'}${numberFormatter.Double(
                                                            Math.abs((tx.close) * tx.currencyAmount),
                                                            2
                                                          )}`
                                                        )}

                                                        {!tx.currencyAmount && tx.entityId !== 'cur.1' && (
                                                          `${tx.nominal < 0 ? '-' : ''}
                                                          ${tx.nominal < 0 ? numberFormatter.Double(Math.abs(tx.price * tx.nominal * (1 + tx.commissionRate)),2) : numberFormatter.Double(Math.abs(tx.close * tx.nominal),2) }`
                                                        )}
                                                      </td>
                                            
                                                      {/* Aksiyon menüsü */}
                                                      <td className="tableCell" style={{ textAlign: 'center' }}>
                                                        <div onClick={(e) => handleTransactionItemMenuToggle(tx.id, e)}>
                                                          <div className={`action-menu-icon ${transactionItemMenuVisible === tx.id ? 'active' : ''}`}>
                                                            <FontAwesomeIcon icon={faEllipsisVertical} className="transactionMenuButtonIcon" />
                                                          </div>
                                                        </div>
                                                    
                                                        {transactionItemMenuVisible === tx.id && (
                                                      <div
                                                        ref={transactionMenuRef}
                                                        className="menu"
                                                        style={{
                                                          position: 'fixed',
                                                          top: transactionItemMenuPosition.top,
                                                          left: transactionItemMenuPosition.left,
                                                          zIndex: 1000
                                                        }}
                                                      >
                                                        <div onClick={() => handleRenameTransaction(tx)} className="menuItem">
                                                          <FontAwesomeIcon icon={faPen} className="action-menu-icon-edit" /> Düzenle
                                                        </div>
                                                        <div className="divider-line"></div>
                                                        <div onClick={() => handleDeleteTransaction(tx)} className="menuItem delete">
                                                          <FontAwesomeIcon icon={faTrash} className="action-menu-icon-delete delete" /> Sil
                                                        </div>
                                                      </div>
                                                    )}

                                                      </td>
                                                    </tr>
                                                  ))}
                                                </tbody>

                                        </table>
                                    )}
                                </Card.Body>
                            </Card>

                            {/* Toplam Tutar Kartı */}
                            {transactionData.length > 0 && (
                                <Card className="portfolioCard bottom">
                                    <Card.Body className="portfolioTotal">
                                        <div className="totalAmountContainer">
                                            <label className="totalAmountTitle">Toplam Tutar</label>
                                            <div className="totalAmount">
                                                {numberFormatter.Double(valuation.latest, 2)} TL
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            )}
                        </>
                    )}
                </Col>

            </Row>

            {menuVisible && (
                <div className="menu" style={{ top: menuPosition.top, left: menuPosition.left }}>
                    <div onClick={() => handleRename(menuVisible)} className="menuItem">
                        <FontAwesomeIcon icon={faPen} className="action-menu-icon-edit" /> Düzenle
                    </div>
                    <div className="divider-line"></div>
                    <div onClick={() => handleDelete(menuVisible)} className="menuItem delete">
                        <FontAwesomeIcon icon={faTrash} className="action-menu-icon-delete" /> Sil
                    </div>
                </div>
            )}


            {/* Modallar */}
            <CreatePortfolioModal
                show={showCreatePortfolioModal}
                onClose={() => setShowCreatePortfolioModal(false)}
                onPortfolioCreated={handlePortfolioCreated}
            />

            <AddTransactionModal
                key={selectedTransaction?.id || 'new'}
                show={showAddTransactionModal}
                handleClose={() => setShowAddTransactionModal(false)}
                onTransactionAdd={() => {
                    fetchPortfoliosTransactionsData(); // Refresh transactions
                    fetchPortfoliosData(); // Refresh portfolios
                }}
                updateMode={!!selectedTransaction}
                transaction={selectedTransaction || {}}
            />

            <RemovePopupModal
                show={showRemoveModal}
                onHide={() => {
                    setShowRemoveModal(false);
                    setSelectedTransactionToDelete(null);
                    setSelectedPortfolioToDelete(null);
                }}
                onDelete={selectedTransactionToDelete ? confirmDeleteTransaction : confirmDelete}
                userName={
                    selectedTransactionToDelete
                        ? selectedTransactionToDelete.code === 'cur.1'
                            ? t('portfolio.cash')
                            : selectedTransactionToDelete.entityCode
                        : (portfolioData.find(p => p.id === selectedPortfolioToDelete)?.name || '')
                }
                content={
                    selectedTransactionToDelete
                        ? selectedTransactionToDelete.code === 'cur.1'
                            ? t('removePopup.removeCashContent')
                            : t('removePopup.removeStockContent')
                        : t('removePopup.confirmDeletePortfolioContent')
                }
                image={selectedTransactionToDelete ? 'danger' : 'user'}
            />




        </div>
    );
};

export default Portfolios;
